import type { DeviceFieldName, DeviceFormValues, DeviceOptions, FuseKind, SelectOption } from '../types/hook-types';

// FuseKind options for "Type beveiliging" dropdown
// "Handmatig" has empty string value to enable custom input mode
export const FUSE_KIND_OPTIONS: SelectOption<FuseKind>[] = [
    { label: 'D-patroon', value: 'D-patroon' },
    { label: 'Mespatroon', value: 'Mespatroon' },
    { label: 'Automaat', value: 'Automaat' },
    { label: 'Handmatig', value: '' },
];

// Known fuse kinds (excluding empty string for "Handmatig")
const KNOWN_FUSE_KINDS: readonly FuseKind[] = ['D-patroon', 'Mespatroon', 'Automaat'];

// TrippingCharacteristic options for "Soort" dropdown - dependent on FuseKind
// Empty string key ('') is for "Handmatig" mode - shows all options combined
export const TRIPPING_CHARACTERISTIC_OPTIONS: Record<FuseKind, SelectOption<string>[]> = {
    'D-patroon': [
        { label: 'Traag gG', value: 'Traag gG' },
        { label: 'DIAZED E16', value: 'DIAZED E16' },
        { label: 'DIAZED snel', value: 'DIAZED snel' },
        { label: 'DIAZED DII, DIII, DIV flink', value: 'DIAZED DII, DIII, DIV flink' },
        { label: 'DIAZED DII, DIII, DIV gG', value: 'DIAZED DII, DIII, DIV gG' },
        { label: 'D-patronen gI/gG', value: 'D-patronen gI/gG' },
        { label: 'Handmatig', value: '' },
    ],
    'Mespatroon': [
        { label: 'gG AC 500V', value: 'gG AC 500V' },
        { label: 'KTF gG 500 VAC', value: 'KTF gG 500 VAC' },
        { label: 'aM AC 500V', value: 'aM AC 500V' },
        { label: 'gI/gG AC 500V', value: 'gI/gG AC 500V' },
        { label: 'Isodin gG/gF 400V', value: 'Isodin gG/gF 400V' },
        { label: 'Isodin gG/gT 500V', value: 'Isodin gG/gT 500V' },
        { label: 'Handmatig', value: '' },
    ],
    'Automaat': [
        { label: 'B-karakteristiek', value: 'B-karakteristiek' },
        { label: 'C-karakteristiek', value: 'C-karakteristiek' },
        { label: 'D-karakteristiek', value: 'D-karakteristiek' },
        { label: 'G-karakteristiek', value: 'G-karakteristiek' },
        { label: 'K-karakteristiek', value: 'K-karakteristiek' },
        { label: 'Z-karakteristiek', value: 'Z-karakteristiek' },
        { label: 'Handmatig', value: '' },
    ],
    // "Handmatig" mode (empty string) - all tripping characteristics available
    '': [
        // D-patroon options
        { label: 'Traag gG', value: 'Traag gG' },
        { label: 'DIAZED E16', value: 'DIAZED E16' },
        { label: 'DIAZED snel', value: 'DIAZED snel' },
        { label: 'DIAZED DII, DIII, DIV flink', value: 'DIAZED DII, DIII, DIV flink' },
        { label: 'DIAZED DII, DIII, DIV gG', value: 'DIAZED DII, DIII, DIV gG' },
        { label: 'D-patronen gI/gG', value: 'D-patronen gI/gG' },
        // Mespatroon options
        { label: 'gG AC 500V', value: 'gG AC 500V' },
        { label: 'KTF gG 500 VAC', value: 'KTF gG 500 VAC' },
        { label: 'aM AC 500V', value: 'aM AC 500V' },
        { label: 'gI/gG AC 500V', value: 'gI/gG AC 500V' },
        { label: 'Isodin gG/gF 400V', value: 'Isodin gG/gF 400V' },
        { label: 'Isodin gG/gT 500V', value: 'Isodin gG/gT 500V' },
        // Automaat options
        { label: 'B-karakteristiek', value: 'B-karakteristiek' },
        { label: 'C-karakteristiek', value: 'C-karakteristiek' },
        { label: 'D-karakteristiek', value: 'D-karakteristiek' },
        { label: 'G-karakteristiek', value: 'G-karakteristiek' },
        { label: 'K-karakteristiek', value: 'K-karakteristiek' },
        { label: 'Z-karakteristiek', value: 'Z-karakteristiek' },
        // "Handmatig" option for nested manual mode
        { label: 'Handmatig', value: '' },
    ],
};

// Rated current (Waarde/In) options from PDF tables - keyed by FuseKind + TrippingCharacteristic
export const RATED_CURRENT_OPTIONS: Record<string, number[]> = {
    // D-patroon options
    'D-patroon:Traag gG': [2, 4, 6, 10, 16, 20, 25, 35, 50, 63, 80, 100],
    'D-patroon:DIAZED E16': [2, 4, 6, 10, 16, 20, 25],
    'D-patroon:DIAZED snel': [2, 4, 6, 10, 16, 20, 25, 35, 50, 63],
    'D-patroon:DIAZED DII, DIII, DIV flink': [2, 4, 6, 10, 16, 20, 25, 35, 50, 63, 80, 100],
    'D-patroon:DIAZED DII, DIII, DIV gG': [2, 4, 6, 10, 16, 20, 25, 32, 35, 50, 63, 80, 100],
    'D-patroon:D-patronen gI/gG': [2, 4, 6, 10, 16, 20, 25, 35, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 355, 400, 500, 630],

    // Mespatroon options
    'Mespatroon:gG AC 500V': [6, 10, 16, 20, 25, 32, 35, 40, 50, 63, 80, 100, 125, 160, 200, 224, 250, 315, 355, 400, 500, 630, 800, 1000, 1250],
    'Mespatroon:KTF gG 500 VAC': [6, 10, 16, 20, 25, 32, 35, 40, 50, 63, 80, 100, 125, 160, 200, 224, 250, 315, 355, 400],
    'Mespatroon:aM AC 500V': [6, 10, 16, 20, 25, 32, 35, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 355, 400, 450, 500, 630, 800, 1000],
    'Mespatroon:gI/gG AC 500V': [2, 4, 6, 10, 16, 20, 25, 32, 35, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 355, 400, 500, 630],
    'Mespatroon:Isodin gG/gF 400V': [10, 16, 25, 35, 50, 63, 80, 100, 125, 160, 200, 224, 250, 315, 355, 400, 500, 630],
    'Mespatroon:Isodin gG/gT 500V': [2, 4, 6, 10, 16, 25, 35, 40, 50, 63, 80, 100, 125, 160, 200],

    // Automaat options - standard circuit breaker ratings
    'Automaat:B-karakteristiek': [2, 4, 6, 10, 13, 16, 20, 25, 32, 40, 50, 63, 80, 100, 125],
    'Automaat:C-karakteristiek': [2, 4, 6, 10, 13, 16, 20, 25, 32, 40, 50, 63, 80, 100, 125],
    'Automaat:D-karakteristiek': [2, 4, 6, 10, 13, 16, 20, 25, 32, 40, 50, 63, 80, 100, 125],
    'Automaat:G-karakteristiek': [2, 4, 6, 10, 13, 16, 20, 25, 32, 40, 50, 63, 80, 100, 125],
    'Automaat:K-karakteristiek': [2, 4, 6, 10, 13, 16, 20, 25, 32, 40, 50, 63, 80, 100, 125],
    'Automaat:Z-karakteristiek': [2, 4, 6, 10, 13, 16, 20, 25, 32, 40, 50, 63, 80, 100, 125],
};

/**
 * Get tripping characteristic options based on fuseKind (FCPD specific).
 * When fuseKind is '' (Handmatig), returns all options combined.
 * When fuseKind is a custom value (not in KNOWN_FUSE_KINDS), returns empty array.
 */
export function getTrippingCharacteristicOptions(fuseKind: FuseKind | string | null): SelectOption<string>[] {
    // Null or undefined - no options
    if (fuseKind === null || fuseKind === undefined) {
        return [];
    }
    // Empty string (Handmatig) or known fuse kind - return mapped options
    if (fuseKind === '' || KNOWN_FUSE_KINDS.includes(fuseKind as FuseKind)) {
        return TRIPPING_CHARACTERISTIC_OPTIONS[fuseKind as FuseKind] ?? [];
    }
    // Custom fuse kind value - no options available
    return [];
}

// All unique rated current values across all configurations (for Handmatig mode)
const ALL_RATED_CURRENT_VALUES = [...new Set(
    Object.values(RATED_CURRENT_OPTIONS).flat(),
)].sort((a, b) => a - b);

/**
 * Get rated current options based on fuseKind and trippingCharacteristic (FCPD specific).
 * When trippingCharacteristic is '' (Handmatig), returns all unique rated current values.
 * When trippingCharacteristic is a custom value (not mapped), returns empty array.
 */
export function getRatedCurrentOptions(
    fuseKind: FuseKind | string | null,
    trippingCharacteristic: string | null,
): SelectOption<number>[] {
    // Null or undefined fuseKind or trippingCharacteristic - no options
    if (fuseKind === null || fuseKind === undefined) {
        return [];
    }
    if (trippingCharacteristic === null || trippingCharacteristic === undefined) {
        return [];
    }

    // If trippingCharacteristic is '' (Handmatig), return all rated current values
    if (trippingCharacteristic === '') {
        return ALL_RATED_CURRENT_VALUES.map(value => ({
            label: `${value} A`,
            value,
        }));
    }

    // Try to find options for the fuseKind:trippingCharacteristic combination
    const key = `${fuseKind}:${trippingCharacteristic}`;
    const values = RATED_CURRENT_OPTIONS[key];

    if (values) {
        return values.map(value => ({
            label: `${value} A`,
            value,
        }));
    }

    // If fuseKind is '' (Handmatig) but trippingCharacteristic is known,
    // try to find options from any fuse kind with that trippingCharacteristic
    if (fuseKind === '') {
        for (const knownFuseKind of KNOWN_FUSE_KINDS) {
            const alternateKey = `${knownFuseKind}:${trippingCharacteristic}`;
            const alternateValues = RATED_CURRENT_OPTIONS[alternateKey];
            if (alternateValues) {
                return alternateValues.map(value => ({
                    label: `${value} A`,
                    value,
                }));
            }
        }
    }

    // No matching options found
    return [];
}

// ============================================================================
// UI Type Helpers for Handmatig Mode
// ============================================================================

// All known tripping characteristic values (across all fuse kinds)
const ALL_KNOWN_TRIPPING_CHARACTERISTICS = new Set(
    Object.values(TRIPPING_CHARACTERISTIC_OPTIONS)
        .flat()
        .map(opt => opt.value)
        .filter(v => v !== ''), // Exclude empty string (Handmatig)
);

// All known rated current values (across all configurations)
const ALL_KNOWN_RATED_CURRENTS = new Set(
    Object.values(RATED_CURRENT_OPTIONS).flat(),
);

/**
 * Check if a value is a known option for the given field.
 * Empty string ('') and null/undefined are NOT considered known options.
 *
 * This determines: SelectField (known) vs InputWithDropdown (unknown/empty)
 */
export function isKnownOption(
    fieldName: 'fuseKind' | 'trippingCharacteristic' | 'breakerRatedCurrent',
    value: string | number | null | undefined,
): boolean {
    // Empty string, null, or undefined = not known
    if (value === '' || value === null || value === undefined) {
        return false;
    }

    switch (fieldName) {
        case 'fuseKind':
            return KNOWN_FUSE_KINDS.includes(value as FuseKind);
        case 'trippingCharacteristic':
            return ALL_KNOWN_TRIPPING_CHARACTERISTICS.has(value as string);
        case 'breakerRatedCurrent':
            return ALL_KNOWN_RATED_CURRENTS.has(value as number);
        default:
            return false;
    }
}

/**
 * Check if parent value is a "known" option (for determining if child gets dropdown options).
 * Empty string ('') is treated as "known" so children still get options in Handmatig mode.
 * Null/undefined means no parent value → no options for child.
 */
export function isKnownParentOption(
    fieldName: 'fuseKind' | 'trippingCharacteristic',
    value: string | number | null | undefined,
): boolean {
    // Null or undefined = no options for child
    if (value === null || value === undefined) {
        return false;
    }
    // Empty string (Handmatig) = child gets options
    if (value === '') {
        return true;
    }
    // Otherwise check if it's a known option
    return isKnownOption(fieldName, value);
}

export type FieldUIType = 'SelectField' | 'InputWithDropdown' | 'TextField' | 'NumberField';

/**
 * Determine which UI component to render for a field based on parent and current values.
 *
 * Logic:
 * 1. If parent exists and is NOT a known option (custom value) → TextField/NumberField
 * 2. If current value is a known option → SelectField
 * 3. Otherwise (empty or custom) → InputWithDropdown
 */
export function getFieldUIType(
    fieldName: 'fuseKind' | 'trippingCharacteristic' | 'breakerRatedCurrent',
    parentValue: string | number | null | undefined,
    currentValue: string | number | null | undefined,
): FieldUIType {
    // fuseKind has no parent, so skip parent check
    if (fieldName === 'fuseKind') {
        if (isKnownOption('fuseKind', currentValue)) {
            return 'SelectField';
        }
        return 'InputWithDropdown';
    }

    // trippingCharacteristic depends on fuseKind
    if (fieldName === 'trippingCharacteristic') {
        // Parent (fuseKind) is custom → no options, use TextField
        if (!isKnownParentOption('fuseKind', parentValue)) {
            return 'TextField';
        }
        // Parent is known or '' (Handmatig), check current value
        if (isKnownOption('trippingCharacteristic', currentValue)) {
            return 'SelectField';
        }
        return 'InputWithDropdown';
    }

    // breakerRatedCurrent depends on trippingCharacteristic
    if (fieldName === 'breakerRatedCurrent') {
        // Parent (trippingCharacteristic) is custom → no options, use NumberField
        if (!isKnownParentOption('trippingCharacteristic', parentValue)) {
            return 'NumberField';
        }
        // Parent is known or '' (Handmatig), check current value
        if (isKnownOption('breakerRatedCurrent', currentValue)) {
            return 'SelectField';
        }
        return 'InputWithDropdown';
    }

    return 'TextField';
}

/**
 * Check if a field should be disabled based on dependencies.
 * With Handmatig mode, fields are only disabled when no parent value is selected at all.
 * The UI type changes (SelectField/InputWithDropdown/TextField) but fields remain enabled.
 */
export function isFieldDisabledByDependency(
    fieldName: DeviceFieldName,
    deviceValues: DeviceFormValues,
): boolean {
    switch (fieldName) {
        case 'trippingCharacteristic':
            // Only disabled if no fuseKind value at all (null/undefined)
            return deviceValues.fuseKind === null || deviceValues.fuseKind === undefined;
        case 'breakerRatedCurrent':
            // Only disabled if no fuseKind OR no trippingCharacteristic
            return (deviceValues.fuseKind === null || deviceValues.fuseKind === undefined)
                || (deviceValues.trippingCharacteristic === null || deviceValues.trippingCharacteristic === undefined);
        default:
            return false;
    }
}

/**
 * Get static options for a field from DeviceOptions (API-provided options).
 */
function getStaticOptionsForField(
    fieldName: DeviceFieldName,
    options: DeviceOptions | undefined,
): SelectOption[] {
    if (!options)
        return [];

    switch (fieldName) {
        case 'residualCurrentDeviceType':
            return options.ResidualCurrentDeviceTypes?.map(t => ({
                label: t.Label ?? t.Value ?? '',
                value: t.Value ?? '',
            })) ?? [];
        case 'ratedCurrent':
            return options.RatedCurrents?.map(rc => ({
                label: rc.Label ?? String(rc.Value),
                value: rc.Value ?? 0,
            })) ?? [];
        case 'trippingCharacteristic':
            // For RCCB - uses API options
            return options.BreakerTrippingCharacteristics?.map(tc => ({
                label: tc.Label ?? tc.Value ?? '',
                value: tc.Value ?? '',
            })) ?? [];
        case 'breakerRatedCurrent':
            // For RCCB - uses API options
            return options.BreakerRatedCurrents?.map(rc => ({
                label: rc.Label ?? String(rc.Value),
                value: rc.Value ?? 0,
            })) ?? [];
        case 'fuseKind':
            return FUSE_KIND_OPTIONS;
        case 'powerPhaseKind':
            return [
                { label: 'Licht', value: '1' },
                { label: 'Kracht', value: '3' },
            ];
        case 'isTestButtonFunctional':
            return [
                { label: 'Ja', value: true },
                { label: 'Nee', value: false },
            ];
        default:
            return [];
    }
}

/**
 * Get options for a field based on current form state.
 * Handles both static options and dynamic options (FCPD dependencies).
 */
export function getFieldOptions(
    fieldName: DeviceFieldName,
    deviceValues: DeviceFormValues,
    options: DeviceOptions | undefined,
    deviceKind: 'ResidualCurrentDevice' | 'ResidualCurrentCircuitBreakerDevice' | 'FinalCircuitProtectiveDevice',
): SelectOption[] {
    // FCPD has special handling for dependent fields
    if (deviceKind === 'FinalCircuitProtectiveDevice') {
        switch (fieldName) {
            case 'trippingCharacteristic':
                return getTrippingCharacteristicOptions(deviceValues.fuseKind);
            case 'breakerRatedCurrent':
                return getRatedCurrentOptions(
                    deviceValues.fuseKind,
                    deviceValues.trippingCharacteristic,
                );
        }
    }

    // All other fields get static options
    return getStaticOptionsForField(fieldName, options);
}
